import mapper from '../../../../_endpoints'
import { usePost } from '../../../../_hooks/usePost'
import { useDelete } from '../../../../_hooks/useDelete'
import { useFetch } from '../../../../_hooks/useFetch'
import { useSasToken } from '../../../../_hooks/useSasToken'
import { toast } from 'react-toastify'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import {
  setChatModels,
  setSelectedAgent,
  setMCPServers
} from '../../../../_redux/actions/chatbotActions'
import { staticAssetsPath } from '../../../../configs/constants'
import { mainChatModels, MCP_SERVER_NAMES } from '../utils/constants'
import { useLocation } from 'react-router-dom'
import { setAgentsList } from '../../../../_redux/actions/agentActions'
import { useDataboxService } from '../../Teams/services/databoxService'

type AgentType = 'Personal' | 'Team' | 'Enterprise'

type AgentListFilters = {
  agentType?: AgentType
  teamScopeIds?: string[]
}

// Utility function to create FormData for agent operations
const createAgentFormData = async (
  data: any,
  getDataboxDataProductsPaginated: (
    databoxId: string | number,
    pageIndex?: number,
    pageSize?: number
  ) => Promise<any>
): Promise<FormData> => {
  const formData = new FormData()

  // Helper to append non-empty values
  const appendIfExists = (key: string, value: any) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value)
    }
  }

  // Text fields mapping
  const textFields = {
    name: data.name,
    description: data.description,
    instructions: data.instructions,
    modelId: data?.model?.id,
    agentType: data.agentType,
    // Enterprise agents aren't scoped to a team/personal databox
    scopeId: data.agentType === 'Enterprise' ? null : data?.scopeId,
    isPersonalAssistant: data?.isPersonalAssistant,
    enableMemory: data?.enableMemory
  }

  // Add text fields
  Object.entries(textFields).forEach(([key, value]) => {
    appendIfExists(key, value || '')
  })

  // Avatar field (binary file or empty)
  if (data.avatarFile instanceof File) {
    // Binary file - new avatar uploaded
    formData.append('avatar', data.avatarFile)
  } else if (data.avatarFile && typeof data.avatarFile === 'string') {
    // No change in avatar - keep existing
    formData.append('removeAvatar', 'false')
  } else {
    // Remove image, use initials
    formData.append('removeAvatar', 'true')
  }

  // Skills — array of { name } objects
  const skillsPayload = Array.isArray(data.skills)
    ? data.skills.map((s: any) => (typeof s === 'string' ? { name: s } : s))
    : []
  formData.append('skills', JSON.stringify(skillsPayload))

  // Define fields to append as an object
  const fieldsToAppend = {
    modelSettings: JSON.stringify(data.modelSettings || {}),
    responseFormat: 'Auto',
    tools: JSON.stringify({})
  }

  if (data?.id) {
    // only in update request - Iterate and append each field
    Object.entries(fieldsToAppend).forEach(([key, value]) => {
      formData.append(key, value)
    })
  }

  // Only append MCP-related fields if agentMcpServers is provided and has changes
  if (data.agentMcpServers !== undefined && data.agentMcpServers !== null) {
    const mcpServers = Array.isArray(data.agentMcpServers)
      ? data.agentMcpServers
      : [data.agentMcpServers]

    // Determine if MCP configuration has actual changes
    // Empty array = cleared all sources (change)
    // Has dataProductScopes/filter/fullySelectedDataboxIds = user made changes
    const hasMcpChanges =
      mcpServers.length === 0 ||
      mcpServers.some((server: any) => {
        const hasDataProductScopes = 'dataProductScopes' in server
        const hasFilter = 'filter' in server
        const hasFullySelectedDataboxes = server.fullySelectedDataboxIds?.length > 0
        const hasOtherMPCs = data.otherMCPs?.includes(server.mcpServerId)
        return hasDataProductScopes || hasFilter || hasFullySelectedDataboxes || hasOtherMPCs
      })

    // Only include in payload if there are actual changes
    if (hasMcpChanges) {
      const transformedMcpServers = await Promise.all(
        mcpServers.map(async (server: any) => {
          // Extract dataProductIds from dataProductScopes, deduplicated
          const dataProductIds = new Set<string>(
            (server.dataProductScopes || []).map((scope: any) => scope.id)
          )

          // Fetch data products for fully-selected databoxes and merge into dataProductIds
          for (const databoxId of server.fullySelectedDataboxIds || []) {
            const response = await getDataboxDataProductsPaginated(databoxId, 1, 50)
            const fetchedProducts = Array.isArray(response) ? response : []
            fetchedProducts.forEach((product: any) => dataProductIds.add(product.id))
          }

          // Extract teamIdentifier (smartDocsTeamId - numeric team ID for backend)
          // Priority: teamId > teamIdentifier from server level
          const teamIdentifier = server.teamId || server.teamIdentifier

          // Build scopeConfiguration - only include fields with values
          const scopeConfiguration: any = {}
          if (teamIdentifier) {
            scopeConfiguration.teamIdentifier = teamIdentifier
          }
          // Add filter if present (OData query string)
          // SmartInsights: filter already fully encodes databox + file scope.
          // Snowflake: only cares about data products (dataProductIds/customSelection below).
          if (server.filter) {
            scopeConfiguration.filters = server.filter
          }
          // Add flat dataProductIds (standalone, databox-nested, and fully-selected databoxes)
          if (dataProductIds.size > 0) {
            scopeConfiguration.dataProductIds = Array.from(dataProductIds)
          }
          // Add customSelection.dataProducts - data products grouped by databox (null = standalone)
          if (server.dataProductGroups?.length > 0) {
            scopeConfiguration.customSelection = {
              dataProducts: server.dataProductGroups
            }
          }

          return {
            mcpServerId: server.mcpServerId,
            scopeConfiguration
          }
        })
      )

      formData.append('agentMcpServers', JSON.stringify(transformedMcpServers))
    }
  }

  // Personal Databox
  if (data.personalDataBoxes !== undefined && data.personalDataBoxes !== null) {
    formData.append('personalDataBoxes', JSON.stringify(data.personalDataBoxes))
  }

  return formData
}

export const useAgentService = () => {
  const [isGettingMyAgents, getAgents] = useFetch()
  const [isGettingTeamsAgents, getTeamAgents] = useFetch('nexus')
  const [, getEnterpriseAgents] = useFetch()
  const [isGettingAgentDetails, getAgentById] = useFetch('nexus')
  const [isUpdatingAgent, updateAgent] = usePost('nexus')
  const [isCreatingAgent, createNewAgent] = usePost('nexus')
  const [isDeletingAgent, deleteAgentById] = useDelete('nexus')
  const [, getAiModels] = useFetch('nexus')
  const [isGettingAllAgents, getAllAssistants] = useFetch('nexus')
  const [isGenerating, generatePrompt] = usePost('nexus')
  const [, getMCPServers] = useFetch('nexus')
  const { getDataboxDataProductsPaginated } = useDataboxService()
  const { appendToken } = useSasToken()
  const aliIcon = appendToken(`${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`)

  const dispatch = useDispatch()
  const location = useLocation()

  // Keeps the latest page location in a ref, so an old API call never reads outdated info
  const latestLocationRef = useRef(location)
  useLayoutEffect(() => {
    latestLocationRef.current = location
  }, [location])

  // Tracks if this page is still open, so a late response can't change things on a different page
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const generateSystemPrompt = async (prompt: any, template?: string) => {
    try {
      const response = await generatePrompt({
        url: mapper.agents.generatePrompt,
        data: {
          prompt,
          template: template === 'systemPrompt' ? 'AgentInstruction' : 'EnhanceDescription'
        }
      })
      return response
    } catch (error) {
      toast.error('Error generating system prompt')
      throw error
    }
  }

  const createAgent = async (data: any) => {
    try {
      const formData = await createAgentFormData(data, getDataboxDataProductsPaginated)

      const response = await createNewAgent({
        url: mapper.agents.createAgent,
        data: formData,
        hasFiles: true
      })
      return response
    } catch (error) {
      toast.error('Error creating agent')
      throw error
    }
  }

  const updateAgentDetails = async (agentId: string, data: any) => {
    const formData = await createAgentFormData(data, getDataboxDataProductsPaginated)

    return await updateAgent({
      url: mapper.agents.updateAgent.replace(':id', agentId),
      data: formData,
      method: 'put',
      hasFiles: true // This tells usePost to handle it as multipart/form-data
    })
  }

  const getAgentsListByUser = async (
    shouldClearSelection: boolean | AgentListFilters = false,
    filters: AgentListFilters = {}
  ) => {
    const clearSelection = typeof shouldClearSelection === 'boolean' ? shouldClearSelection : false
    const listFilters = typeof shouldClearSelection === 'boolean' ? filters : shouldClearSelection

    // Only clear selection if explicitly requested (e.g., after deleting an agent)
    if (clearSelection && !latestLocationRef.current.state?.fromAgentCard) {
      dispatch(setSelectedAgent(null))
    }
    const query = new URLSearchParams()
    if (listFilters.agentType) query.set('agentType', listFilters.agentType)
    listFilters.teamScopeIds?.forEach((teamScopeId) => query.append('teamScopeId', teamScopeId))
    const queryString = query.toString()
    const agentsUrl = `${mapper.agents.getMyAgents}${queryString ? `?${queryString}` : ''}`
    const response = await getAgents(agentsUrl)
    if (response?.agents && response?.agents?.length > 0) {
      dispatch(
        setAgentsList(
          response?.agents.map((assistant: any) => ({
            ...assistant,
            avatarUrl: assistant?.name.toLowerCase().includes('centrali')
              ? aliIcon
              : assistant?.avatarUrl
          }))
        )
      )

      // Check the page is still the same one and still open before using this response
      const currentLocation = latestLocationRef.current
      const urlParams = new URLSearchParams(currentLocation.search)
      const chatId = urlParams.get('id') || currentLocation.pathname.includes('/chat/')

      // Only default to the personal assistant for a new chat, unless opened from an agent card.
      if (isMountedRef.current && !currentLocation.state?.fromAgentCard && !chatId) {
        const personalAssistant = response?.agents?.find((agent: any) => agent?.isPersonalAssistant)
        if (personalAssistant) {
          dispatch(setSelectedAgent(personalAssistant))
        }
      }
    } else {
      dispatch(setAgentsList(null))
    }

    return response?.agents || []
  }

  const getAllAgents = async (isDataMarketplace?: boolean) => {
    const response = await getAllAssistants(mapper.agents.getAllAssistants)
    if (response?.agents && response?.agents?.length > 0) {
      // Same check as above - skip picking a default agent if the page already moved on
      const fromAgentCard = latestLocationRef.current.state?.fromAgentCard
      //  for data market place, by default selecting dedicated DataProductAgent agent
      if (isMountedRef.current && !fromAgentCard && isDataMarketplace) {
        const dataProductAgent = response?.agents?.find(
          (agent: any) => agent?.name === 'DataProductAgent'
        )
        if (dataProductAgent) {
          dispatch(setSelectedAgent({ ...dataProductAgent }))
        }
      } else if (isMountedRef.current && !fromAgentCard) {
        const centraliAgent = response?.agents?.find((agent: any) => agent?.name === 'Centrali')
        if (centraliAgent) {
          dispatch(setSelectedAgent({ ...centraliAgent, avatarUrl: aliIcon }))
        }
      }
    } else {
      dispatch(setAgentsList(null))
    }
  }

  const getAgentDetails = async (agentId: string) => {
    return await getAgentById(mapper.agents.getAgentById.replace(':id', agentId))
  }

  const getModels = async () => {
    const response = await getAiModels(mapper.agents.getModels)
    if (response?.models && response?.models?.length > 0) {
      const updatedModels = response.models.map((model: any) => {
        return mainChatModels.includes(model.name)
          ? { ...model, isMainModel: true }
          : { ...model, isMainModel: false }
      })
      dispatch(setChatModels(updatedModels))
    } else {
      dispatch(setChatModels(null))
    }
  }

  const deleteAgent = async (agentId: string) => {
    return await deleteAgentById({
      url: mapper.agents.deleteAgent.replace(':id', agentId)
    })
  }

  const getTeamAgentsList = async (teamId: string) => {
    return await getTeamAgents(mapper.agents.getTeamAgents.replace(':teamId', teamId))
  }

  // The Community team isn't scoped like other teams - it shows all enterprise agents instead.
  const getEnterpriseAgentsList = async () => {
    return await getEnterpriseAgents(mapper.agents.getEnterpriseAgents)
  }

  const getAllMCPServers = async () => {
    const resp = await getMCPServers(mapper.mcpServer.getAll)
    const allMcpServers = resp?.mcpServers || []
    // The PersonalUnstructured server is reserved for the Personal Databox tab's own scope
    const filteredMcpServers = allMcpServers.filter(
      (server: any) => server?.name !== MCP_SERVER_NAMES.PERSONAL_UNSTRUCTURED
    )
    dispatch(setMCPServers(filteredMcpServers))
    return allMcpServers
  }

  return {
    isGettingMyAgents,
    getAgentsListByUser,
    isGettingTeamsAgents,
    getTeamAgentsList,
    getEnterpriseAgentsList,
    isGettingAgentDetails,
    getAgentDetails,
    isCreatingAgent,
    createAgent,
    isUpdatingAgent,
    updateAgentDetails,
    getModels,
    isDeletingAgent,
    deleteAgent,
    isGenerating,
    generateSystemPrompt,
    isGettingAllAgents,
    getAllAgents,
    getAllMCPServers
  }
}
